import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const COUNTRIES = [
  { k: "ES", l: "España" },
  { k: "PT", l: "Portugal" },
  { k: "BR", l: "Brasil" },
  { k: "MX", l: "México" },
  { k: "AR", l: "Argentina" },
  { k: "CO", l: "Colombia" },
  { k: "CL", l: "Chile" },
  { k: "FR", l: "France" },
  { k: "IT", l: "Italia" },
];

const inp = {
  width: "100%", background: "#111", border: "1px solid #2a2a2a", borderRadius: 10,
  padding: "12px 14px", color: "#fff", fontSize: 14, outline: "none",
  boxSizing: "border-box",
};
const lbl = { display: "block", color: "#888", fontSize: 12, fontWeight: 700, marginBottom: 6 };

function Field({ id, label, type = "text", value, onChange, placeholder, required }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={lbl}>{label}{required && <span style={{ color: "#22c55e" }}> *</span>}</label>
      <input id={id} type={type} value={value} onChange={onChange} placeholder={placeholder}
        required={required} style={inp} />
    </div>
  );
}

function SelectField({ id, label, value, onChange, options, required }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={id} style={lbl}>{label}{required && <span style={{ color: "#22c55e" }}> *</span>}</label>
      <select id={id} value={value} onChange={onChange} required={required}
        style={{ ...inp, appearance: "none", cursor: "pointer" }}>
        {options.map(o => <option key={o.k} value={o.k}>{o.l}</option>)}
      </select>
    </div>
  );
}

export default function RestaurantSignupPage() {
  const [params] = useSearchParams();
  const ref = (params.get("ref") || "").trim().toUpperCase();
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useTranslation();

  const [form, setForm] = useState({
    restaurant_name: "", name: "", email: "",
    whatsapp: "", country: "ES", password: "", confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError(t("login.passwordsNotMatch")); return; }
    if (form.password.length < 8) { setError(t("login.passwordMinLength")); return; }
    setLoading(true);
    try {
      await register(form.email.trim(), form.password, form.name.trim(),
        form.restaurant_name.trim(), ref, form.country);
      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || t("signup.signupError");
      setError(Array.isArray(msg) ? msg.map(m => m.msg).join("; ") : msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ background: "#0a0a0a", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ color: "#4ade80", fontSize: 20, fontWeight: 800 }}>{t("login.accountCreated")}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#0a0a0a", minHeight: "100vh", fontFamily: "'Inter',sans-serif", color: "#f5f5f5", padding: "40px 20px 80px" }}>
      <div style={{ maxWidth: 520, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 40 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg,#22c55e,#16a34a)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚡</div>
            <span style={{ fontWeight: 800, fontSize: 16, color: "#fff" }}>Kaiso System</span>
          </div>
          <LanguageSelector variant="minimal" />
        </div>

        {/* Referral badge */}
        {ref && (
          <div style={{ background: "#1a2e1a", border: "1px solid #22c55e44", borderRadius: 12, padding: "12px 16px", marginBottom: 28, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20 }}>🤝</span>
            <div>
              <div style={{ color: "#4ade80", fontSize: 12, fontWeight: 700 }}>{t("signup.partnerInvite")}</div>
              <div style={{ color: "#999", fontSize: 12 }}>{t("signup.referralCodeLabel")} <strong style={{ color: "#fff" }}>{ref}</strong></div>
            </div>
          </div>
        )}

        {/* Heading */}
        <h1 style={{ fontSize: "clamp(22px,5vw,32px)", fontWeight: 900, color: "#fff", margin: "0 0 8px", lineHeight: 1.2 }}>
          {t("login.createAccount")}<br />
          <span style={{ color: "#22c55e" }}>Kaiso System</span>
        </h1>
        <p style={{ color: "#777", fontSize: 14, marginBottom: 32, lineHeight: 1.6 }}>
          {t("login.createAccountDesc")}
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ background: "#111", border: "1px solid #1e1e1e", borderRadius: 16, padding: "28px 24px" }}>

          <Field id="restaurant_name" label={t("login.restaurantName")} value={form.restaurant_name}
            onChange={set("restaurant_name")} placeholder={t("login.restaurantPlaceholder")} required />

          <Field id="name" label={t("signup.ownerName")} value={form.name}
            onChange={set("name")} placeholder={t("login.namePlaceholder")} required />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field id="email" label="Email" type="email" value={form.email}
              onChange={set("email")} placeholder={t("login.emailPlaceholder")} required />
            <Field id="whatsapp" label={t("signup.whatsapp")} type="tel" value={form.whatsapp}
              onChange={set("whatsapp")} placeholder="+34 600 000 000" />
          </div>

          <SelectField id="country" label={t("signup.country")} value={form.country}
            onChange={set("country")} options={COUNTRIES} required />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field id="password" label={t("signup.password")} type="password" value={form.password}
              onChange={set("password")} placeholder="••••••••" required />
            <Field id="confirm" label={t("login.confirmPassword")} type="password" value={form.confirm}
              onChange={set("confirm")} placeholder="••••••••" required />
          </div>

          {ref && (
            <div style={{ background: "#0a0a0a", border: "1px solid #333", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#666" }}>
              {t("signup.referralCodeLabel")} <strong style={{ color: "#4ade80" }}>{ref}</strong> {t("signup.referralApplied")}
            </div>
          )}

          {error && (
            <div style={{ background: "#2a0a0a", border: "1px solid #ef444433", borderRadius: 8, padding: "10px 14px", marginBottom: 16, color: "#f87171", fontSize: 13 }}>
              {error}
            </div>
          )}

          <button type="submit" disabled={loading}
            style={{ width: "100%", background: loading ? "#166534" : "linear-gradient(135deg,#22c55e,#16a34a)", color: "#000", padding: "14px 0", borderRadius: 10, fontWeight: 800, fontSize: 15, border: "none", cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 4px 20px #22c55e44" }}>
            {loading ? t("signup.creating") : t("signup.ctaButton")}
          </button>

          <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "#555" }}>
            <Link to="/login" style={{ color: "#4ade80", textDecoration: "none", fontWeight: 700 }}>
              {t("login.hasAccount")}
            </Link>
          </p>
        </form>

        {/* Trust signals */}
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16, marginTop: 28 }}>
          {[t("signup.trust1"), t("signup.trust2"), t("signup.trust3"), t("signup.trust4")].map((text, i) => (
            <span key={i} style={{ color: "#555", fontSize: 12 }}>{text}</span>
          ))}
        </div>

      </div>
    </div>
  );
}
